import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs';
import {
  Card,
  Row,
  Col,
  Button,
  DatePicker,
  Select,
  Form,
  Table,
  Statistic,
  Space,
  Typography,
  Tag,
  Alert,
  Spin,
  message,
  Tooltip,
  Segmented,
  Divider,
  Input
} from 'antd';

import {
  DownloadOutlined,
  BarChartOutlined,
  TrophyOutlined,
  RiseOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  PrinterOutlined,
  CalendarOutlined,
  TeamOutlined,
  UserOutlined,
  DollarCircleOutlined,
  FilterOutlined,
  SearchOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';

import apiClient from '../utils/apiClient';
import './SalesReports.css';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title, Text } = Typography;

const SalesReports = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [exportingFormat, setExportingFormat] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [reportStats, setReportStats] = useState({
    totalRevenue: 0,
    totalBookings: 0,
    totalLeads: 0,
    conversionRate: 0,
    averageValue: 0
  });

  // Date selection mode: 'range' | 'month' | 'year' | 'preset'
  const [dateMode, setDateMode] = useState('range');

  // Date states
  const [dateRange, setDateRange] = useState([dayjs().startOf('month'), dayjs().endOf('month')]);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [selectedYear, setSelectedYear] = useState(dayjs());
  const [selectedPreset, setSelectedPreset] = useState('this_month');

  // Filters
  const [reportType, setReportType] = useState('sales');
  const [teamId, setTeamId] = useState(null);
  const [userId, setUserId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    fetchTeams();
    fetchUsers();
    generateReport();
  }, []);

  const fetchTeams = async () => {
    try {
      const response = await apiClient.get('/teams');
      const data = response.data?.data || response.data || [];
      setTeams(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching teams:', error);
      setTeams([]);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiClient.get('/users');
      const data = response.data?.data || response.data || [];
      setUsers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  // Compute actual start and end dates based on dateMode
  const getComputedDateRange = () => {
    try {
      if (dateMode === 'range') {
        if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
          return {
            startDate: dayjs.isDayjs(dateRange[0]) ? dateRange[0].startOf('day') : dayjs(dateRange[0]).startOf('day'),
            endDate: dayjs.isDayjs(dateRange[1]) ? dateRange[1].endOf('day') : dayjs(dateRange[1]).endOf('day'),
            label: `${dayjs(dateRange[0]).format('DD MMM YYYY')} - ${dayjs(dateRange[1]).format('DD MMM YYYY')}`
          };
        }
      } else if (dateMode === 'month') {
        if (selectedMonth) {
          const m = dayjs.isDayjs(selectedMonth) ? selectedMonth : dayjs(selectedMonth);
          return {
            startDate: m.startOf('month'),
            endDate: m.endOf('month'),
            label: m.format('MMMM YYYY')
          };
        }
      } else if (dateMode === 'year') {
        if (selectedYear) {
          const y = dayjs.isDayjs(selectedYear) ? selectedYear : dayjs(selectedYear);
          return {
            startDate: y.startOf('year'),
            endDate: y.endOf('year'),
            label: y.format('YYYY')
          };
        }
      } else if (dateMode === 'preset') {
        const now = dayjs();
        if (selectedPreset === 'today') {
          return { startDate: now.startOf('day'), endDate: now.endOf('day'), label: 'Today' };
        } else if (selectedPreset === 'this_week') {
          return { startDate: now.startOf('week'), endDate: now.endOf('week'), label: 'This Week' };
        } else if (selectedPreset === 'this_month') {
          return { startDate: now.startOf('month'), endDate: now.endOf('month'), label: 'This Month' };
        } else if (selectedPreset === 'last_month') {
          const lm = now.subtract(1, 'month');
          return { startDate: lm.startOf('month'), endDate: lm.endOf('month'), label: 'Last Month' };
        } else if (selectedPreset === 'this_quarter') {
          return { startDate: now.startOf('quarter'), endDate: now.endOf('quarter'), label: 'This Quarter' };
        } else if (selectedPreset === 'this_year') {
          return { startDate: now.startOf('year'), endDate: now.endOf('year'), label: 'This Year' };
        }
      }
    } catch (e) {
      console.error("Error computing date range:", e);
    }

    const defaultMonth = dayjs();
    return {
      startDate: defaultMonth.startOf('month'),
      endDate: defaultMonth.endOf('month'),
      label: defaultMonth.format('MMMM YYYY')
    };
  };

  const generateReport = async () => {
    setLoading(true);
    try {
      const { startDate, endDate } = getComputedDateRange();
      
      const queryParams = new URLSearchParams({
        reportType: reportType,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });

      if (teamId) queryParams.append('teamId', teamId);
      if (userId) queryParams.append('userId', userId);
      if (statusFilter && statusFilter !== 'all') queryParams.append('status', statusFilter);

      let fetchedData = [];
      let statsObj = null;

      try {
        const response = await apiClient.get(`/reports/sales?${queryParams}`);
        if (response.data) {
          fetchedData = response.data.data || [];
          statsObj = response.data.stats || response.data.summary;
        }
      } catch (err) {
        console.warn('Backend endpoint /reports/sales failed, fetching fallback leads directly:', err);
        // Fallback to fetching leads directly if /reports/sales is empty or errors
        const leadsRes = await apiClient.get('/leads');
        const rawLeads = Array.isArray(leadsRes.data)
          ? leadsRes.data
          : Array.isArray(leadsRes.data?.data)
          ? leadsRes.data.data
          : [];
        
        // Filter leads in client side by date
        fetchedData = rawLeads.filter(lead => {
          if (!lead.createdAt) return true;
          const leadDate = dayjs(lead.createdAt);
          return leadDate.isAfter(startDate) && leadDate.isBefore(endDate);
        });
      }

      setReportData(Array.isArray(fetchedData) ? fetchedData : []);

      // Calculate statistics safely
      const items = Array.isArray(fetchedData) ? fetchedData : [];
      const totalLeads = items.length;
      const totalBookings = items.filter(i => i.status === 'confirmed' || i.status === 'converted' || i.status === 'completed').length;
      const totalRevenue = items.reduce((sum, item) => sum + Number(item.quotedAmount || item.revenue || item.totalAmount || item.budget?.max || 0), 0);
      const conversionRate = totalLeads > 0 ? Number(((totalBookings / totalLeads) * 100).toFixed(1)) : 0;
      const averageValue = totalLeads > 0 ? Math.round(totalRevenue / totalLeads) : 0;

      setReportStats(statsObj ? {
        totalRevenue: statsObj.totalRevenue ?? totalRevenue,
        totalBookings: statsObj.totalBookings ?? totalBookings,
        totalLeads: statsObj.totalLeads ?? totalLeads,
        conversionRate: statsObj.conversionRate ?? conversionRate,
        averageValue: statsObj.averageValue ?? averageValue
      } : {
        totalRevenue,
        totalBookings,
        totalLeads,
        conversionRate,
        averageValue
      });

    } catch (error) {
      console.error('Error generating report:', error);
      message.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  // Download Report Handler (Excel / CSV / PDF / Print)
  const exportReport = async (format) => {
    setExportingFormat(format);
    const { startDate, endDate, label } = getComputedDateRange();
    const fileName = `sales_report_${label.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD')}`;

    try {
      if (format === 'print') {
        window.print();
        setExportingFormat(null);
        return;
      }

      // Try server-side export first
      try {
        const queryParams = new URLSearchParams({
          reportType: reportType,
          format: format,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });
        if (teamId) queryParams.append('teamId', teamId);
        if (userId) queryParams.append('userId', userId);

        const response = await apiClient.get(`/reports/export?${queryParams}`, {
          responseType: 'blob'
        });

        if (response.data && response.data.size > 0) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `${fileName}.${format === 'excel' ? 'xlsx' : format}`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          message.success(`Report exported as ${format.toUpperCase()}`);
          setExportingFormat(null);
          return;
        }
      } catch (serverErr) {
        console.info('Server export unavailable, utilizing client-side exporter:', serverErr);
      }

      // Client-Side CSV/Excel Fallback Exporter
      const dataToExport = Array.isArray(reportData) ? reportData : [];
      if (dataToExport.length === 0) {
        message.warning('No data available in current date range to export.');
        setExportingFormat(null);
        return;
      }

      const headers = ["Date", "Lead ID", "Customer Name", "Phone", "Destination", "Status", "Quoted Amount (₹)", "Assigned To"];
      const rows = dataToExport.map(item => {
        const itemDate = item.createdAt ? dayjs(item.createdAt).format('YYYY-MM-DD') : '';
        const leadId = item.leadId || item._id || '';
        const custName = (item.customerName || item.customer?.name || '').replace(/"/g, '""');
        const phone = (item.phone || item.phoneNumber || '').replace(/"/g, '""');
        const dest = (item.destinationSnapshot || item.destination?.name || (typeof item.destination === 'string' ? item.destination : '')).replace(/"/g, '""');
        const status = item.status || '';
        const amount = item.quotedAmount || item.revenue || item.totalAmount || item.budget?.max || 0;
        const assigned = (item.assignedTo?.name || (typeof item.assignedTo === 'string' ? item.assignedTo : '')).replace(/"/g, '""');

        return [
          itemDate,
          leadId,
          `"${custName}"`,
          `"${phone}"`,
          `"${dest}"`,
          status,
          amount,
          `"${assigned}"`
        ].join(',');
      });

      const csvContent = "data:text/csv;charset=utf-8,\uFEFF" + [headers.join(','), ...rows].join('\n');
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement('a');
      link.setAttribute('href', encodedUri);
      link.setAttribute('download', `${fileName}.${format === 'excel' ? 'csv' : format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      message.success(`Report downloaded successfully as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting report:', error);
      message.error('Could not complete download');
    } finally {
      setExportingFormat(null);
    }
  };

  const computedRangeInfo = getComputedDateRange();

  // Filtered table data for search
  const filteredReportData = (Array.isArray(reportData) ? reportData : []).filter(item => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const cust = (item.customerName || '').toLowerCase();
    const phone = (item.phone || '').toLowerCase();
    const leadId = (item.leadId || '').toLowerCase();
    const dest = (item.destinationSnapshot || (typeof item.destination === 'string' ? item.destination : item.destination?.name) || '').toLowerCase();
    return cust.includes(term) || phone.includes(term) || leadId.includes(term) || dest.includes(term);
  });

  const reportColumns = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (date ? dayjs(date).format('DD MMM YYYY') : '-')
    },
    {
      title: 'Lead / Query ID',
      dataIndex: 'leadId',
      key: 'leadId',
      render: (id, record) => <span style={{ fontWeight: 600, color: '#4f46e5' }}>{id || record._id?.substring(0, 8)}</span>
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{name || 'N/A'}</div>
          {record.phone && <div style={{ fontSize: 11, color: '#64748b' }}>{record.phone}</div>}
        </div>
      )
    },
    {
      title: 'Destination',
      dataIndex: 'destination',
      key: 'destination',
      render: (dest, record) => (
        <Tag color="geekblue">
          {record.destinationSnapshot || (typeof dest === 'string' ? dest : dest?.name) || 'Not Specified'}
        </Tag>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const s = String(status || '').toLowerCase();
        let color = 'default';
        if (['confirmed', 'converted', 'completed'].includes(s)) color = 'green';
        else if (['quoted', 'proposal', 'negotiation'].includes(s)) color = 'blue';
        else if (['contacted', 'qualified'].includes(s)) color = 'orange';
        else if (['lost', 'cancelled'].includes(s)) color = 'red';
        return <Tag color={color}>{s.toUpperCase() || 'NEW'}</Tag>;
      }
    },
    {
      title: 'Quoted Amount',
      dataIndex: 'quotedAmount',
      key: 'quotedAmount',
      render: (amt, record) => {
        const val = amt || record.revenue || record.budget?.max || 0;
        return <span style={{ fontWeight: 600, color: '#059669' }}>₹{Number(val).toLocaleString()}</span>;
      }
    },
    {
      title: 'Assigned To',
      dataIndex: 'assignedTo',
      key: 'assignedTo',
      render: (assigned) => (typeof assigned === 'object' ? assigned?.name : assigned) || 'Unassigned'
    }
  ];

  return (
    <div className="sales-reports" style={{ padding: '24px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
        <div>
          <Title level={2} style={{ margin: 0, color: '#1e293b' }}>
            <BarChartOutlined style={{ marginRight: 10, color: '#4f46e5' }} />
            Sales & Analytics Reports
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            Generate, filter, and download detailed sales, bookings, and performance reports
          </Text>
        </div>

        <Tag color="indigo" style={{ padding: '6px 14px', fontSize: 13, borderRadius: 8 }}>
          <CalendarOutlined style={{ marginRight: 6 }} />
          <strong>Active Filter Period:</strong> {computedRangeInfo.label}
        </Tag>
      </div>

      {/* Summary Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={4}>
          <Card bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 13 }}>Total Leads</span>}
              value={reportStats.totalLeads}
              prefix={<UserOutlined style={{ color: '#3b82f6' }} />}
              valueStyle={{ fontWeight: 700, fontSize: 22, color: '#1e293b' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={5}>
          <Card bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 13 }}>Total Bookings</span>}
              value={reportStats.totalBookings}
              prefix={<TrophyOutlined style={{ color: '#059669' }} />}
              valueStyle={{ fontWeight: 700, fontSize: 22, color: '#059669' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={5}>
          <Card bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 13 }}>Total Revenue</span>}
              value={reportStats.totalRevenue}
              prefix={<DollarCircleOutlined style={{ color: '#10b981' }} />}
              precision={0}
              formatter={(val) => `₹${Number(val).toLocaleString()}`}
              valueStyle={{ fontWeight: 700, fontSize: 22, color: '#10b981' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={5}>
          <Card bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 13 }}>Conversion Rate</span>}
              value={reportStats.conversionRate}
              prefix={<RiseOutlined style={{ color: '#8b5cf6' }} />}
              suffix="%"
              precision={1}
              valueStyle={{ fontWeight: 700, fontSize: 22, color: '#8b5cf6' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={5}>
          <Card bodyStyle={{ padding: 16 }}>
            <Statistic
              title={<span style={{ color: '#64748b', fontSize: 13 }}>Average Deal Value</span>}
              value={reportStats.averageValue}
              prefix={<BarChartOutlined style={{ color: '#ec4899' }} />}
              precision={0}
              formatter={(val) => `₹${Number(val).toLocaleString()}`}
              valueStyle={{ fontWeight: 700, fontSize: 22, color: '#ec4899' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Primary Report Controls Card */}
      <Card
        title={
          <Space>
            <FilterOutlined style={{ color: '#4f46e5' }} />
            <span style={{ fontWeight: 700, fontSize: 16 }}>Report Filters & Date Selection</span>
          </Space>
        }
        style={{ marginBottom: 24, borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
      >
        <Form layout="vertical">
          {/* Row 1: Date Selection Mode Selector */}
          <div style={{ marginBottom: 20, padding: 14, background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0' }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: '#475569', marginBottom: 10 }}>
              <CalendarOutlined style={{ marginRight: 6, color: '#4f46e5' }} />
              Choose Date Selection Mode:
            </div>
            
            <Segmented
              value={dateMode}
              onChange={(value) => setDateMode(value)}
              options={[
                { label: '📅 Custom Date Range', value: 'range' },
                { label: '🗓️ Select Month', value: 'month' },
                { label: '📆 Select Year', value: 'year' },
                { label: '⚡ Quick Presets', value: 'preset' },
              ]}
              style={{ marginBottom: 16 }}
            />

            {/* Dynamic Date Inputs based on Date Mode */}
            <div>
              {dateMode === 'range' && (
                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>
                    Select Start and End Dates from Calendar:
                  </Text>
                  <RangePicker
                    value={dateRange}
                    onChange={(dates) => {
                      setDateRange(dates);
                    }}
                    format="DD MMM YYYY"
                    style={{ width: '100%', maxWidth: 360 }}
                    allowClear={false}
                  />
                </div>
              )}

              {dateMode === 'month' && (
                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>
                    Select Specific Month:
                  </Text>
                  <DatePicker
                    picker="month"
                    value={selectedMonth}
                    onChange={(m) => setSelectedMonth(m || dayjs())}
                    format="MMMM YYYY"
                    style={{ width: '100%', maxWidth: 280 }}
                    allowClear={false}
                  />
                </div>
              )}

              {dateMode === 'year' && (
                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>
                    Select Specific Year:
                  </Text>
                  <DatePicker
                    picker="year"
                    value={selectedYear}
                    onChange={(y) => setSelectedYear(y || dayjs())}
                    format="YYYY"
                    style={{ width: '100%', maxWidth: 240 }}
                    allowClear={false}
                  />
                </div>
              )}

              {dateMode === 'preset' && (
                <div>
                  <Text type="secondary" style={{ display: 'block', marginBottom: 6, fontSize: 12 }}>
                    Select Preset Time Period:
                  </Text>
                  <Select
                    value={selectedPreset}
                    onChange={(val) => setSelectedPreset(val)}
                    style={{ width: '100%', maxWidth: 260 }}
                  >
                    <Option value="today">Today</Option>
                    <Option value="this_week">This Week</Option>
                    <Option value="this_month">This Month</Option>
                    <Option value="last_month">Last Month</Option>
                    <Option value="this_quarter">This Quarter</Option>
                    <Option value="this_year">This Year</Option>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Row 2: Secondary Filters */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Report Type" style={{ marginBottom: 0 }}>
                <Select value={reportType} onChange={setReportType} style={{ width: '100%' }}>
                  <Option value="sales">Sales & Revenue Report</Option>
                  <Option value="leads">Leads Performance Report</Option>
                  <Option value="bookings">Confirmed Bookings Report</Option>
                  <Option value="conversion">Conversion Rate Report</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Team" style={{ marginBottom: 0 }}>
                <Select
                  value={teamId}
                  onChange={setTeamId}
                  placeholder="All Teams"
                  allowClear
                  style={{ width: '100%' }}
                >
                  {Array.isArray(teams) &&
                    teams.map((t) => (
                      <Option key={t._id} value={t._id}>
                        {t.name}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Sales Owner / Agent" style={{ marginBottom: 0 }}>
                <Select
                  value={userId}
                  onChange={setUserId}
                  placeholder="All Sales Owners"
                  allowClear
                  style={{ width: '100%' }}
                >
                  {Array.isArray(users) &&
                    users.map((u) => (
                      <Option key={u._id} value={u._id}>
                        {u.name || u.email}
                      </Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6}>
              <Form.Item label="Lead / Trip Status" style={{ marginBottom: 0 }}>
                <Select value={statusFilter} onChange={setStatusFilter} style={{ width: '100%' }}>
                  <Option value="all">All Statuses</Option>
                  <Option value="contacted">Contacted</Option>
                  <Option value="qualified">Qualified</Option>
                  <Option value="quoted">Quoted</Option>
                  <Option value="converted">Confirmed / Converted</Option>
                  <Option value="lost">Lost</Option>
                  <Option value="cancelled">Cancelled</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Divider style={{ margin: '16px 0' }} />

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={generateReport}
              loading={loading}
              style={{ background: '#4f46e5', borderColor: '#4f46e5', minWidth: 150 }}
            >
              Generate Report
            </Button>
          </div>
        </Form>
      </Card>

      {/* Prominent Download Options Card */}
      <Card
        title={
          <Space>
            <DownloadOutlined style={{ color: '#059669', fontSize: 18 }} />
            <span style={{ fontWeight: 700, fontSize: 16 }}>Download & Export Options</span>
          </Space>
        }
        extra={
          <Tag color="green">
            <CheckCircleOutlined style={{ marginRight: 4 }} /> Ready to Export
          </Tag>
        }
        style={{
          marginBottom: 24,
          borderRadius: 12,
          border: '1px solid #cbd5e1',
          background: 'linear-gradient(to right, #ffffff, #f0fdf4)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <Text style={{ fontWeight: 600, fontSize: 14, color: '#334155' }}>
              Export options for selected period ({computedRangeInfo.label}):
            </Text>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              Download complete dataset containing {reportData.length} records in your preferred format
            </div>
          </div>

          <Space size="middle" wrap>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={() => exportReport('excel')}
              loading={exportingFormat === 'excel'}
              style={{ background: '#16a34a', borderColor: '#16a34a', fontWeight: 600 }}
            >
              Download Excel (.xlsx)
            </Button>

            <Button
              icon={<DownloadOutlined />}
              onClick={() => exportReport('csv')}
              loading={exportingFormat === 'csv'}
              style={{ borderColor: '#2563eb', color: '#2563eb', fontWeight: 600 }}
            >
              Download CSV (.csv)
            </Button>

            <Button
              icon={<FilePdfOutlined />}
              onClick={() => exportReport('pdf')}
              loading={exportingFormat === 'pdf'}
              style={{ borderColor: '#dc2626', color: '#dc2626', fontWeight: 600 }}
            >
              Download PDF Report
            </Button>

            <Button
              icon={<PrinterOutlined />}
              onClick={() => exportReport('print')}
              style={{ fontWeight: 500 }}
            >
              Print / Save
            </Button>
          </Space>
        </div>
      </Card>

      {/* Report Data Table */}
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <span>Detailed Report Dataset</span>
            <Input
              placeholder="Search in report data..."
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 260 }}
              allowClear
            />
          </div>
        }
        style={{ borderRadius: 12 }}
      >
        <Table
          columns={reportColumns}
          dataSource={filteredReportData}
          rowKey={(record) => record._id || record.leadId || Math.random()}
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`
          }}
          scroll={{ x: 900 }}
        />
      </Card>
    </div>
  );
};

export default SalesReports;
